import React from 'react';
import BoardNodeContainer, {
  BoardNodeContainerProps,
} from '../BoardNodeContainer';
import { NodeProps } from 'reactflow';
import { Box } from '@mui/material';
import { NODE_MIN_WIDTH } from './constants';
import type { BoardNodes } from 'types/BoardNode';
import {
  transformSparkBoardNodeToRectFlowNodes,
  useReactFlowHelpers,
} from './useReactFlowHelpers';
import { useBoardNodesContext } from 'hooks/common';
import { useBoardNodeTypes } from 'hooks/EntityTypes/useBoardNodeTypes';
import ComponentOverlay from 'sharedComponents/Overlay/ComponentOverlay';
import BoardNodeSelectionWrapper from './BoardNodeSelectionWrapper';
import EmptyNodeContainer from './EmptyNodeContainer';
export type SparkStepNodeProps = {
  data: BoardNodeContainerProps;
} & NodeProps;

const CustomNodeWrapper = ({ data }: SparkStepNodeProps) => {
  const [isHover, setIsHover] = React.useState(false);
  const { updateReactFlowNode, removeFromReactFlowNodes, addToReactFlowNodes } =
    useReactFlowHelpers();
  const { selectedNodes, setSelectedNodes } = useBoardNodesContext();
  const { idMap: boardNodeTypesIdMap } = useBoardNodeTypes();

  const scrollableRef = React.useRef<HTMLDivElement | null>(null);
  const boardNode = React.useMemo(() => {
    if (data.boardNode) {
      return data.boardNode as BoardNodes;
    }
  }, [data.boardNode]);
  React.useEffect(() => {
    // this check if the react flow node has any scrollable content, then the panning stops
    // currently only checking the menu with class .ce-popover__container
    // add more selectors as needed
    const handleWheel = (event: WheelEvent) => {
      if (!scrollableRef.current) return;
      const target = event.target as Node;
      const popper = scrollableRef.current.querySelector(
        '.ce-popover__container'
      );
      const codePanel = scrollableRef.current.querySelector('.node-code-panel');
      if (popper?.contains(target) || codePanel?.contains(target)) {
        event.stopPropagation(); // Prevent React Flow from scrolling
      }
    };

    const element = scrollableRef.current;
    element?.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      element?.removeEventListener('wheel', handleWheel);
    };
  }, []);

  const handleUpdateNode = React.useCallback(
    (node: BoardNodes) => {
      updateReactFlowNode(transformSparkBoardNodeToRectFlowNodes(node));
    },
    [updateReactFlowNode]
  );

  const handleDeleteNode = React.useCallback(
    (nodeID: string) => {
      removeFromReactFlowNodes(nodeID);
    },
    [removeFromReactFlowNodes]
  );

  const handleDuplicateNode = React.useCallback(
    (node: BoardNodes) => {
      addToReactFlowNodes(transformSparkBoardNodeToRectFlowNodes(node));
    },
    [addToReactFlowNodes]
  );

  const handleClickAway = React.useCallback(
    (e: MouseEvent | TouchEvent) => {
      console.log('boardNode', boardNode?.id);
      if (e.shiftKey) {
        return;
      }
      setSelectedNodes([]);
    },
    [boardNode?.id, setSelectedNodes]
  );
  const getIsNodeSelected = React.useCallback(
    (nodeID: string) => {
      return selectedNodes.includes(nodeID);
    },
    [selectedNodes]
  );

  if (!boardNode) {
    return null;
  }
  const isEmpty = !boardNode?.meta?.data;
  const nodeType =
    boardNodeTypesIdMap?.[boardNode?.attributes.boardNodeTypeID || ''];

  const isOverlayDisabled =
    nodeType?.attributes.key === 'audio' ||
    nodeType?.attributes.key === 'video' ||
    nodeType?.attributes.key === 'file';

  return (
    <Box
      minWidth={NODE_MIN_WIDTH}
      width={NODE_MIN_WIDTH}
      height='100%'
      component='div'
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
      ref={scrollableRef}
      sx={{
        backfaceVisibility: 'hidden',
        pointerEvents: data?.draggable ? 'all' : 'none',
      }}
    >
      <BoardNodeSelectionWrapper nodeID={boardNode.id} isEmpty={isEmpty}>
        <ComponentOverlay
          disabledOverlay={isOverlayDisabled}
          onClickAway={handleClickAway}
          overlayClassName='drag-handle'
          render={() => (
            <>
              {isEmpty ? (
                <EmptyNodeContainer
                  isSelected={getIsNodeSelected(boardNode.id)}
                  nodeType={nodeType}
                  isAddingNewNode={!data.draggable}
                  boardNode={boardNode}
                  handleUpdateNode={handleUpdateNode}
                />
              ) : (
                <BoardNodeContainer
                  {...data}
                  boardNode={boardNode}
                  boardNodeTitle={boardNode.attributes.title}
                  readOnly={false}
                  containerProps={{ className: 'node-container' }}
                  isHover={isHover}
                  onUpdateNode={handleUpdateNode}
                  onDeleteNode={handleDeleteNode}
                  onDuplicateNode={handleDuplicateNode}
                  nodeType={nodeType}
                  isNodeSelected={getIsNodeSelected(boardNode.id)}
                />
              )}
            </>
          )}
        />
      </BoardNodeSelectionWrapper>
    </Box>
  );
};

export default React.memo(CustomNodeWrapper);
